import React, {useCallback, useRef, useState} from 'react';
import {createPortal} from 'react-dom';

export interface DateTooltipEntry {
  label: string;
  value: string;
  color: string;
}

export const DateTooltip: React.FC<{dates: DateTooltipEntry[]; children: React.ReactNode}> = ({dates, children}) => {
  const [visible, setVisible] = useState(false);
  const [pos, setPos] = useState({top: 0, left: 0});
  const timerRef = useRef<ReturnType<typeof setTimeout>>(undefined);
  const triggerRef = useRef<HTMLDivElement>(null);

  const handleMouseEnter = useCallback(() => {
    timerRef.current = setTimeout(() => {
      if (triggerRef.current) {
        const rect = triggerRef.current.getBoundingClientRect();
        setPos({top: rect.top - 8, left: rect.left + rect.width / 2});
      }
      setVisible(true);
    }, 250);
  }, []);

  const handleMouseLeave = useCallback(() => {
    clearTimeout(timerRef.current);
    setVisible(false);
  }, []);

  React.useEffect(() => () => clearTimeout(timerRef.current), []);

  return (
    <div ref={triggerRef} onMouseEnter={handleMouseEnter} onMouseLeave={handleMouseLeave}>
      {children}
      {visible &&
        createPortal(
          <div
            className="fixed -translate-x-1/2 -translate-y-full px-3 py-2 bg-white rounded-lg shadow-lg border border-gray-200 z-50 whitespace-nowrap text-sm pointer-events-none"
            style={{top: pos.top, left: pos.left}}>
            {dates.map((d) => (
              <div key={d.label} className="flex flex-row justify-between gap-4">
                <span className={`font-medium ${d.color}`}>{d.label}</span>
                <span className="text-gray-600">{d.value}</span>
              </div>
            ))}
          </div>,
          document.body,
        )}
    </div>
  );
};
